import { useState, useMemo, useEffect, useRef } from "react";
import "./home.css";
import SEO from "../../Components/SEO";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNewUser } from "../../APIContext/NewUserContext";
import Loading from "../../Components/LoaderNew";
import noImage from "../../Assets/Images/no-pictures.png";

const Home = () => {
  const navigate = useNavigate();
  const { fetchPracticeListing, fetchLocations } = useNewUser();

  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const itemsPerPage = 10;

  const formatLocation = (value = "") =>
    value
      ?.replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        // Fetch listings and locations in parallel
        const [res, locationsRes] = await Promise.all([
          fetchPracticeListing(),
          fetchLocations(),
        ]);

        setData(Array.isArray(res) ? res : res?.data || []);

        if (locationsRes?.locations) {
          setLocations(locationsRes.locations);
        }
      } catch (err) {
        console.error("Failed to fetch data", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [fetchPracticeListing, fetchLocations]);

  /* ===============================
     Close dropdown on outside click
  ================================ */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLocationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ===============================
     Filters — filter by slug
  ================================ */
  const filteredListings = useMemo(() => {
    if (selectedLocations.length === 0) return data;
    return data.filter((item) =>
      selectedLocations.includes(item.listing_location),
    );
  }, [selectedLocations, data]);

  /* ===============================
     Pagination
  ================================ */
  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredListings.slice(start, start + itemsPerPage);
  }, [filteredListings, currentPage]);

  /* ===============================
     Handlers
  ================================ */
  const handleLocationToggle = (slug) => {
    setSelectedLocations((prev) =>
      prev.includes(slug) ? prev.filter((l) => l !== slug) : [...prev, slug],
    );
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleProceed = () => {
    navigate(`/listing?ids=${selectedIds.join(",")}`);
  };

  const getFirstImage = (value) => {
    if (!value) return null;

    if (value === "[]") return null;
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
        return null;
      } catch (err) {
        const urls = value.split(",").map((url) => url.trim());
        if (urls.length > 0 && urls[0] !== "") {
          return urls[0];
        }
      }
    }

    return null; // Final fallback
  };

  // Build trigger label
  const triggerLabel = () => {
    if (selectedLocations.length === 0) return "All Locations";
    if (selectedLocations.length === 1) {
      const match = locations.find((l) => l.slug === selectedLocations[0]);
      return match ? match.name : selectedLocations[0];
    }
    return `${selectedLocations.length} locations selected`;
  };
  return (
    <>
      <SEO
        title="Listings - Hudson Transition Partner Inc"
        description=""
        url=""
      />

      <div className="home-page">
        <div className="announcement-bar">
          <h1>LISTINGS</h1>
        </div>

        <div className="container">
          {/* Header */}
          <div className="header">
            <div className="filter-box">
              <label className="filter-label">Select a Location:</label>

              {/* Multi-select dropdown */}
              <div className="multiselect-wrapper" ref={dropdownRef}>
                <button
                  className="filter-select multiselect-trigger"
                  onClick={() => setLocationDropdownOpen((prev) => !prev)}
                >
                  <span>{triggerLabel()}</span>
                  <span className="dropdown-arrow">▾</span>
                </button>

                {locationDropdownOpen && (
                  <div className="multiselect-dropdown">
                    {locations.length === 0 ? (
                      <div className="multiselect-empty">
                        No locations available
                      </div>
                    ) : (
                      locations.map((loc) => (
                        <label key={loc.id} className="multiselect-option">
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(loc.slug)}
                            onChange={() => handleLocationToggle(loc.slug)}
                          />
                          {loc.name} {/* Name from API, no formatting needed */}
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="footer-info">
              Listing • Showing {currentItems.length} of{" "}
              {filteredListings.length} items
            </div>
          </div>

          {/* Table Header */}
          <div className="table-header">
            <div>Select</div>
            <div>Title</div>
            <div>Location</div>
            <div>City</div>
            <div>Practice Type</div>
            <div>Ref#</div>
            <div></div>
          </div>

          {/* Loading State */}
          {loading && <Loading text="Loading listings..." />}

          {/* Table Body */}
          {!loading && (
            <div className="table-body">
              {currentItems.length > 0 ? (
                currentItems.map((item) => {
                  // Find the matching location object
                  const matchedLocation = locations.find(
                    (l) => l.slug === item.listing_location,
                  );

                  // Extract listing specific image
                  const listingImage = getFirstImage(item.data_room);

                  // Priority Logic: Listing Image -> Location Default -> Fallback
                  const displayImage = listingImage
                    ? listingImage
                    : matchedLocation?.default_image
                      ? matchedLocation.default_image
                      : noImage;

                  return (
                    <div className="table-row" key={item.record_id}>
                      <div className="image-cell">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.record_id)}
                          onChange={() => handleCheckboxChange(item.record_id)}
                        />
                        <img
                          src={displayImage}
                          alt={item.listing_title}
                          className="listing-img"
                        />
                      </div>

                      <div className="listing-title" data-label="Title">
                        {item.listing_title}
                      </div>
                      <div className="listing-text" data-label="Location">
                        {/* Use the matched location name, fallback to formatLocation */}
                        {matchedLocation?.name ||
                          formatLocation(item.listing_location)}
                      </div>
                      <div className="listing-text" data-label="City">
                        {formatLocation(item?.city) || "-"}
                      </div>
                      <div className="listing-text" data-label="Practice Type">
                        {formatLocation(item.practice_listing_type)}
                      </div>
                      <div className="listing-text" data-label="Ref#">
                        {item.listing_ref_code || "N/A"}
                      </div>
                      <div className="action-cell">
                        <button
                          className="secondary-btn"
                          onClick={() =>
                            navigate(`/listing?ids=${item.record_id}`)
                          }
                        >
                          Learn More
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">
                  No listings found for this selection.
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="page-arrow"
              >
                <ChevronLeft size={18} />
              </button>

              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={
                    currentPage === i + 1 ? "page-number active" : "page-number"
                  }
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="page-arrow"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          {/* Proceed Bar */}
          {!loading && selectedIds.length > 0 && (
            <div className="proceed-bar">
              <span>{selectedIds.length} selected</span>
              <button className="primary-btn" onClick={handleProceed}>
                Proceed
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
